import React from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

import Markdown from 'react-markdown';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import { ButtonRow, FormCheckBox, useTrackedFormik } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../../../layout/Portal';
import ROUTES from '../../../../../../core/constants/routes.constant';
import { useAppSelector } from '../../../../../../store';
import { withdrawalValuesSelector } from '../../../../../../store/participant/selectors';
import { chartColours } from '../../../../../../core/constants/colours.constants';

function WithdrawalConfirm1Scene() {
  const values = useAppSelector(withdrawalValuesSelector);
  const navigate = useNavigate();
  const { t } = useTranslation('portal');

  const validationSchema = yup.object({
    confirm: yup.boolean().oneOf([true], t('withdrawal4.validation')),
  });

  const formik = useTrackedFormik(
    {
      initialValues: {
        confirm: false,
      },
      validationSchema,
      onSubmit: () => {
        navigate(ROUTES.portalWithdrawalConfirm2);
      },
    },
    'withdrawal-confirm-1',
  );

  return (
    <PortalLayout>
      <Box
        sx={{
          maxWidth: 980,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              pt: { xs: 2.5, md: 3.5 },
              pb: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${chartColours[5]}26`,
                  color: chartColours[5],
                  flexShrink: 0,
                  border: 1,
                  borderColor: `${chartColours[5]}55`,
                }}
              >
                <WarningAmberOutlined sx={{ fontSize: 34 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.15,
                  }}
                >
                  {t('withdrawal4.title')}
                </Typography>

                <Typography
                  variant="body1"
                  component="p"
                  color="text.secondary"
                  sx={{
                    mt: 0.75,
                    maxWidth: 780,
                    fontWeight: 700,
                  }}
                >
                  {t('withdrawal4.subtitle')}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <form onSubmit={formik.handleSubmit}>
              <Paper
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    pt: { xs: 2, md: 2.5 },
                    pb: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${chartColours[0]}26`,
                        color: chartColours[0],
                        border: 1,
                        borderColor: `${chartColours[0]}55`,
                        flexShrink: 0,
                      }}
                    >
                      <FactCheckOutlined sx={{ fontSize: 28 }} />
                    </Box>

                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 900,
                        lineHeight: 1.2,
                      }}
                    >
                      {t('withdrawal4.subtitle')}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: { xs: 2, md: 2.5 },
                  }}
                >
                  <Typography
                    variant="body1"
                    component="p"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      lineHeight: 1.6,
                    }}
                  >
                    {t('withdrawal4.para1')}
                  </Typography>

                  <Typography
                    variant="body1"
                    component="p"
                    color="text.secondary"
                    sx={{
                      mb: 2.5,
                      lineHeight: 1.6,
                    }}
                  >
                    {t('withdrawal4.para2')}
                  </Typography>

                  {values && values.withdrawalOption && (
                    <Paper
                      elevation={0}
                      sx={{
                        mb: 3,
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 3,
                        border: 1,
                        borderColor: `${chartColours[5]}55`,
                        bgcolor: `${chartColours[5]}12`,
                      }}
                    >
                      <Box
                        sx={{
                          '& p': {
                            m: 0,
                            fontWeight: 900,
                            lineHeight: 1.5,
                          },
                        }}
                      >
                        <Markdown>{t(`withdrawal2.options.${values.withdrawalOption}.label`)}</Markdown>
                      </Box>
                    </Paper>
                  )}

                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                      '& label': {
                        fontWeight: 800,
                      },
                    }}
                  >
                    <FormCheckBox
                      name="confirm"
                      value={formik.values.confirm}
                      error={formik.errors.confirm}
                      touched={formik.touched.confirm}
                      label={t('withdrawal4.confirm')}
                      onChange={formik.handleChange}
                    />
                  </Paper>
                </Box>
              </Paper>

              <Box
                sx={{
                  '& button': {
                    borderRadius: 999,
                    fontWeight: 900,
                    textTransform: 'none',
                  },
                }}
              >
                <ButtonRow
                  backLabel={t('withdrawal4.back')}
                  forwardLabel={t('withdrawal4.button')}
                  forwardColor="error"
                  buttonSize="large"
                />
              </Box>
            </form>
          </Box>
        </Paper>
      </Box>
    </PortalLayout>
  );
}

export default WithdrawalConfirm1Scene;
